/**
 * Canonicals (Column Mapping) Screen
 *
 * Maps raw source column names from uploaded files to standardized
 * "canonical" field names (e.g., "Inv Date" -> "Invoice Date",
 * "Cust Name" -> "Buyer Parent Name"). This mapping is required before
 * the master frame can be built, since all downstream analysis expects
 * canonical column names.
 *
 * Interaction model:
 *  - Drag-and-drop: canonical tags (right panel) are dragged onto source
 *    column rows (left table). Clicking a mapped row removes the mapping.
 *  - AI suggestion: "AI Suggest Mapping" sends source headers + sample
 *    values to the backend, which returns per-column mapping suggestions
 *    with confidence levels. User can accept individually or bulk-accept.
 *
 * Data flow:
 *  - Operates per-collection (selected via the Collections sidebar).
 *  - `getSamples` fetches column headers and sample values from raw CSVs.
 *  - Mapping stored in `collection.mapping` (source -> canonical).
 *  - `canonicals` (from Data/canonicals.js) defines mandatory and optional
 *    canonical fields that the system expects.
 */
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import Collections from "Components/Collections";
// ------------------------------------------------------
import { getSamples } from "Redux/files/actions";
import { suggestMapping } from "Redux/data/actions";
import { removeCanonical, updateCanonical } from "Redux/collections/actions";
// ------------------------------------------------------
import { canonicals } from "Data/canonicals.js";
import waitbar from "Media/waitbar.gif";

const CONFIDENCE_STYLE = {
  high: { backgroundColor: "#D8F5E8", color: "#1B7A4A" },
  medium: { backgroundColor: "#FFF3D6", color: "#8A6A17" },
  low: { backgroundColor: "#FDE2E2", color: "#B22525" },
};

// ------------------------------------------------------
// ------------------------------------------------------
const Canonicals = () => {
  const dispatch = useDispatch();
  const [samples, setSamples] = useState(null);
  const [collection, setCollection] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const { current, collections, repository } = useSelector(
    (state) => state.project
  );

  useEffect(() => {
    const collection = collections.find((c) => c.id === current);
    setCollection(collection);
  }, [current, collections]);

  useEffect(() => {
    setSamples(null);
    setSuggestions(null);
    const collection = collections.find((c) => c.id === current);
    if (collection) {
      const files = collection.fileids.map((fileid) => repository[fileid]);
      getSamples(files, (res) => {
        setSamples(res);
      });
    }
  }, [current, collections, repository]);

  if (!collection) return <Collections />;

  const custom = collection.custom || [];
  const mapping = collection.mapping || {};
  const reverseMapping = Object.keys(mapping).reduce((acc, key) => {
    acc[mapping[key]] = key;
    return acc;
  }, {});

  // Build suggestion lookup: source column -> { canonical, confidence, reason }.
  // Normalized keys handle cases where the LLM alters spacing/casing in its response.
  const suggestionMap = {};
  const normalize = (s) => (s || "").trim().toLowerCase();
  if (suggestions) {
    suggestions.forEach((s) => {
      if (s.source) {
        suggestionMap[normalize(s.source)] = s;
      }
    });
  }
  const getSuggestion = (header) => suggestionMap[normalize(header)];

  const handleSuggest = () => {
    if (!samples) return;
    setSuggesting(true);
    dispatch(
      suggestMapping(samples.headers, canonicals, samples.sample, (result) => {
        setSuggesting(false);
        if (result.mappings) {
          setSuggestions(result.mappings);
        }
      })
    );
  };

  const handleAcceptSuggestion = (sourceCol, canonicalName) => {
    dispatch(updateCanonical(current, canonicalName, sourceCol));
  };

  const handleAcceptAll = () => {
    if (!suggestions) return;
    suggestions.forEach((s) => {
      if (s.canonical && !mapping[s.source]) {
        dispatch(updateCanonical(current, s.canonical, s.source));
      }
    });
  };

  return (
    <Collections>
      <div className="columns">
        <div className="column extend-page">
          {samples === null ? (
            <div>
              <img src={waitbar} alt="Loading" />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "0.75rem",
                }}
              >
                <button
                  className={`button is-small ${suggesting ? "is-loading" : ""}`}
                  onClick={handleSuggest}
                  disabled={suggesting}
                  style={{ borderRadius: "6px" }}
                >
                  <span className="icon" style={{ fontSize: "0.8rem" }}>
                    <i className="fa-duotone fa-wand-magic-sparkles" />
                  </span>
                  <span>AI Suggest Mapping</span>
                </button>
                {suggestions && suggestions.length > 0 && (
                  <>
                    <button
                      className="button is-small is-success"
                      onClick={handleAcceptAll}
                      style={{ borderRadius: "6px" }}
                    >
                      <span className="icon" style={{ fontSize: "0.8rem" }}>
                        <i className="fa-duotone fa-check-double" />
                      </span>
                      <span>Accept All ({suggestions.length})</span>
                    </button>
                    <span style={{ fontSize: "0.7rem", color: "var(--ev-text-muted)" }}>
                      Matched: {samples?.headers?.filter((h) => getSuggestion(h)).length || 0} / {suggestions.length}
                    </span>
                  </>
                )}
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Map</th>
                      <th style={{ whiteSpace: "nowrap" }}>AI&nbsp;Suggestion</th>
                      <th>Sample</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custom.length > 0 ? (
                      <>
                        {custom.map((column, index) => (
                          <CanonicalsRows
                            key={`custom-row-${index}`}
                            header={column.key}
                            mapping={mapping}
                            value={column.value}
                            suggestion={getSuggestion(column.key)}
                            onAccept={handleAcceptSuggestion}
                          />
                        ))}
                        <tr>
                          <td colSpan={4} className="table-divider"></td>
                        </tr>
                      </>
                    ) : null}

                    {samples.headers.map((header, index) => (
                      <CanonicalsRows
                        key={`header-row-${index}`}
                        header={header}
                        mapping={mapping}
                        samples={samples}
                        suggestion={getSuggestion(header)}
                        onAccept={handleAcceptSuggestion}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="column is-one-third">
          <div>Mandatory</div>
          <br />
          <div className="tags">
            {canonicals
              .filter((x) => x.isMandatory)
              .sort((a, b) => (a.header > b.header ? 1 : -1))
              .map((canonical, index) => (
                <Canonical
                  key={canonical.header}
                  canonical={canonical}
                  reverseMapping={reverseMapping}
                />
              ))}
          </div>
          <hr />
          <div>Non-Mandatory</div>
          <br />
          <div className="tags">
            {canonicals
              .filter((x) => !x.isMandatory)
              .sort((a, b) => (a.header > b.header ? 1 : -1))
              .map((canonical, index) => (
                <Canonical
                  key={canonical.header}
                  canonical={canonical}
                  reverseMapping={reverseMapping}
                />
              ))}
          </div>
        </div>
      </div>
    </Collections>
  );
};

export default Canonicals;

// Row for a single source column. Acts as a drop target for canonical tags.
// Displays current mapping (if any), AI suggestion with accept button,
// and sample data values for the column.
const CanonicalsRows = ({ header, mapping, samples, value, suggestion, onAccept }) => {
  const dispatch = useDispatch();

  const { current } = useSelector((state) => state.project);
  const onDragOver = (evt) => {
    evt.preventDefault();
  };

  const isAlreadyMapped = !!mapping[header];

  return (
    <tr
      onDragOver={onDragOver}
      onDrop={(evt) => {
        const canonical = evt.dataTransfer.getData("canonical");
        dispatch(updateCanonical(current, canonical, header));
      }}
    >
      <td>
        {mapping[header] ? (
          <div
            className="tag is-warning is-light table-tag is-clickable"
            onClick={() => {
              dispatch(removeCanonical(current, header));
            }}
          >
            {header}
          </div>
        ) : (
          header
        )}
      </td>
      <td>
        {mapping[header] ? (
          <div
            className="tag is-success is-light table-tag is-clickable"
            onClick={() => {
              dispatch(removeCanonical(current, header));
            }}
          >
            {mapping[header]}
          </div>
        ) : null}
      </td>
      <td>
        {suggestion ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <span
              className="tag"
              style={{
                fontWeight: 600,
                fontSize: "0.72rem",
                borderRadius: "6px",
                backgroundColor: "var(--ev-purple-light)",
                color: "var(--ev-purple-dark)",
                border: "1px solid var(--ev-border)",
              }}
            >
              {suggestion.canonical}
            </span>
            <span
              className="tag"
              style={{
                fontSize: "0.6rem",
                fontWeight: 700,
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                ...(CONFIDENCE_STYLE[suggestion.confidence] || {}),
              }}
            >
              {suggestion.confidence}
            </span>
            {!isAlreadyMapped && (
              <button
                className="button is-small is-success is-outlined"
                onClick={() => onAccept(header, suggestion.canonical)}
                style={{ borderRadius: "4px", padding: "0 0.4em", height: "22px", fontSize: "0.65rem" }}
                title={suggestion.reason}
              >
                <span className="icon" style={{ fontSize: "0.65rem" }}>
                  <i className="fa-solid fa-check" />
                </span>
              </button>
            )}
          </div>
        ) : null}
      </td>
      <td>{samples ? <Samples samples={samples} header={header} /> : value}</td>
    </tr>
  );
};

// Draggable canonical field tag. Shown as "taken" (non-draggable) when
// already mapped to a source column, otherwise draggable for assignment.
const Canonical = ({ canonical, reverseMapping }) => {
  const onDragStart = (evt) => {
    evt.dataTransfer.setData("canonical", canonical.header);
  };

  const thisClass = `tag ${canonical.isMandatory ? "is-info" : ""}`;
  if (reverseMapping[canonical.header]) {
    return <div className={`${thisClass} is-taken`}>{canonical.header}</div>;
  }
  return (
    <div className={`${thisClass} is-draggable`} draggable onDragStart={onDragStart}>
      {canonical.header}
    </div>
  );
};

// ------------------------------------------------------
// ------------------------------------------------------
const Samples = ({ samples, header }) => {
  const specimen = samples.sample[header];
  return (
    <div className="tags">
      {specimen.map((x, i) => (
        <div key={`${header}-${i}`} className="tag is-tiny">
          {x}
        </div>
      ))}
    </div>
  );
};
